/**
 * Evals Service for handling eval export and run API calls.
 * Uses API key auth (x-api-key header) instead of JWT.
 */

import { getEnvConfig } from './serviceConfig';

import type { EvalRunOverrides, EvalRunResponse, EvalScenarioPrimitives, ExportResponse } from 'app/types/evals';

type EvalsServiceConfig = {
  onError?: (error: Error) => void;
};

export class EvalsApiError extends Error {
  status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = 'EvalsApiError';
    this.status = status;
  }
}

export function createEvalsService(config: EvalsServiceConfig = {}) {
  const defaultErrorHandler = config.onError || ((error: Error) => console.error('Evals service error:', error));

  function getBaseUrl(): string {
    const envConfig = getEnvConfig();
    return `${envConfig.baseUrl}${envConfig.apiPrefix}`;
  }

  /**
   * Export conversations as eval scenarios.
   * POST /eval/export
   */
  async function exportConversations(conversationIds: string[], apiKey: string): Promise<ExportResponse> {
    try {
      const response = await fetch(`${getBaseUrl()}/eval/export`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': apiKey,
        },
        body: JSON.stringify({ conversationIds }),
      });

      if (!response.ok) {
        let errorMessage = `Export error: ${response.status}`;
        try {
          const errorData = await response.json();
          if (errorData.message) {
            errorMessage = `Export error: ${errorData.message}`;
          }
        } catch {
          // Use status code as error message
        }
        throw new EvalsApiError(errorMessage, response.status);
      }

      return response.json();
    } catch (error) {
      defaultErrorHandler(error as Error);
      throw error;
    }
  }

  /**
   * Run eval scenarios against the AI agent.
   * POST /eval/run (multipart form-data)
   */
  async function runEvalScenarios(
    scenarios: EvalScenarioPrimitives[],
    apiKey: string,
    overrides?: EvalRunOverrides,
    turn?: number
  ): Promise<EvalRunResponse> {
    try {
      const formData = new FormData();
      const scenariosBlob = new Blob([JSON.stringify(scenarios)], { type: 'application/json' });
      formData.append('scenarios', scenariosBlob, 'scenarios.json');

      if (overrides && Object.keys(overrides).length > 0) {
        formData.append('overrides', JSON.stringify(overrides));
      }

      if (turn !== undefined) {
        formData.append('turn', String(turn));
      }

      const response = await fetch(`${getBaseUrl()}/eval/run`, {
        method: 'POST',
        headers: {
          'x-api-key': apiKey,
        },
        body: formData,
      });

      if (!response.ok) {
        let errorMessage = `Run error: ${response.status}`;
        try {
          const errorData = await response.json();
          if (errorData.message) {
            errorMessage = `Run error: ${errorData.message}`;
          }
        } catch {
          // Use status code as error message
        }
        throw new EvalsApiError(errorMessage, response.status);
      }

      return response.json();
    } catch (error) {
      defaultErrorHandler(error as Error);
      throw error;
    }
  }

  /**
   * Validate an API key by making a test request to the eval endpoint.
   * Returns true if the key passes the ApiKeyGuard (any non-401 response).
   */
  async function validateApiKey(apiKey: string): Promise<boolean> {
    try {
      const response = await fetch(`${getBaseUrl()}/eval/export`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': apiKey,
        },
        body: JSON.stringify({ conversationIds: ['00000000-0000-0000-0000-000000000000'] }),
      });

      return response.status !== 401;
    } catch {
      return false;
    }
  }

  return { exportConversations, runEvalScenarios, validateApiKey };
}

export default createEvalsService();
